/* site/edit/editPaths.js — the bridge between what you SEE and what you EDIT.
 *
 * Every visible string on the public site comes from a content module through
 * useSiteContent(key, defaults), so editing the site means writing a value at a path like
 *   landing.quad.items.2.name        or        settings.footer.columns.0.links.1.label
 *
 * The pages render those values as plain JSX, so the path is not carried into the DOM. This module
 * recovers it by VALUE: index every string leaf of the content tree, then match a clicked element's
 * text against that index. Repeated strings ("Sofas" appears in several places) are disambiguated by
 * ORDINAL — the n-th element on screen with that text is the n-th leaf with that value, because the
 * page renders content in tree order. Anything still ambiguous is reported as a list of candidates
 * so the UI can ask rather than guess.
 *
 * Pure and DOM-light on purpose: everything here is unit-testable.
 */

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
export const MEDIA_RE = /^(https?:)?\/\/|^\/(uploads|assets)\//i;
const IMAGE_RE = /\.(png|jpe?g|webp|gif|avif|svg)(\?|#|$)/i;
const VIDEO_RE = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

/** what kind of value is this, for the editor's affordances */
export function leafKind(value) {
  const s = String(value == null ? "" : value);
  if (MEDIA_RE.test(s)) {
    if (VIDEO_RE.test(s)) return "video";
    if (IMAGE_RE.test(s) || /picsum\.photos/.test(s)) return "image";
    return "link";
  }
  if (/^#|\.html(\?|#|$)/.test(s)) return "link";
  return "text";
}

/** Flatten a content tree to its editable leaves, in tree order. */
export function buildLeafIndex(content) {
  const out = [];
  const walk = (node, path) => {
    if (typeof node === "string" || typeof node === "number" || typeof node === "boolean") {
      out.push({ path: path.join("."), value: node, kind: leafKind(node) });
      return;
    }
    if (Array.isArray(node)) { node.forEach((v, i) => walk(v, path.concat(String(i)))); return; }
    if (isObj(node)) { Object.keys(node).forEach((k) => walk(node[k], path.concat(k))); }
  };
  Object.keys(content || {}).forEach((section) => walk(content[section], [section]));
  return out;
}

/* Which content sections can this route possibly render? Without this the editor can match a string
   to an identical one belonging to ANOTHER page ("The 2026 Collection" lives in landing AND in
   collections) and silently edit the wrong page. `settings`/`worlds`/`landing` are always in scope
   because the chrome (nav, mega menu, footer) is on every page. */
const ALWAYS = ["settings", "worlds", "landing"];
export function sectionsForRoute(pathname) {
  const p = String(pathname || "/").replace(/\/+$/, "") || "/";
  const seg = p.split("/").filter(Boolean);
  const add = (...k) => Array.from(new Set(ALWAYS.concat(k)));
  if (p === "/") return add();
  if (p === "/collections") return add("collections");
  if (p === "/materials") return add("materials");
  if (seg[0] === "materials") return add("materials", "materialDetail");
  if (p === "/projects") return add("projects");
  if (seg[0] === "projects") return add("projects", "projectDetail");
  if (seg[0] === "partners") return add("partnerAccount");
  if (["contract", "about", "contact", "resources", "legal"].includes(seg[0])) return add(seg[0]);
  if (["kitchens", "systems", "indoor", "outdoor", "products"].includes(seg[0])) {
    /* a world hub or a category listing is the catalog; anything deeper is a piece */
    if (seg.length === 1 || (seg.length === 2 && seg[0] !== "kitchens")) return add("catalog");
    return add("catalog", "piece", "kitchenDetail", "productDetail");
  }
  return add();
}

/** value → [paths], for O(1) lookup of what a piece of text could be. */
/* `title` is NOT here: on this site it is the visible heading (introBand.title, connect.title).
   Only <head> titles are hidden, and those live under meta.* which HIDDEN_BRANCH already catches.
   `label` is demoted because the content layer uses it for aria labels as often as for copy. */
const HIDDEN_KEY = /(^|\.)(label|scrollLabel|heading)$/;
const HIDDEN_BRANCH = /(^|\.)meta\./;
/** lower sorts first: real on-page copy beats an alt text or a <head> value with the same words */
export function leafRank(path) {
  if (HIDDEN_BRANCH.test(path)) return 3;
  if (/(^|\.)(alt|imageAlt|ariaLabel|aria)$/.test(path)) return 2;
  if (HIDDEN_KEY.test(path)) return 1;
  return 0;
}

/** value → [leaves], best candidate first. `sections` limits it to what this route can render. */
export function indexByValue(leaves, sections) {
  const allow = sections && sections.length ? new Set(sections) : null;
  const map = new Map();
  leaves.forEach((leaf) => {
    if (allow && !allow.has(String(leaf.path).split(".")[0])) return;
    const key = normalise(leaf.value);
    if (!key) return;
    const list = map.get(key);
    if (list) list.push(leaf); else map.set(key, [leaf]);
  });
  map.forEach((list) => list.sort((a, b) => leafRank(a.path) - leafRank(b.path)));
  return map;
}

export const normalise = (v) => String(v == null ? "" : v).replace(/\s+/g, " ").trim();

/** read a path out of a content object */
export function getAt(content, path) {
  return String(path).split(".").reduce((o, k) => (o == null ? undefined : o[k]), content);
}

/**
 * Immutably write `value` at `path` into an overrides object.
 *
 * `base` is the CURRENT merged content, and it matters: the content layer merges objects key by key
 * but REPLACES arrays wholesale. So an override that touches one item of a list must carry the whole
 * list, or saving "the 3rd card's image" would publish a one-item list and delete the other cards.
 * Objects stay sparse (only what changed is stored); arrays are copied from `base` and then patched.
 */
export function setAt(overrides, path, value, base) {
  const keys = String(path).split(".");
  /* `sealed` means "we are inside an array". Because arrays REPLACE on merge, the array we write
     must be complete AND so must the objects inside it — a sparse item would publish a card with
     only the field you touched. Outside an array, objects deep-merge, so the override stays sparse. */
  const walk = (over, bas, i, sealed) => {
    const key = keys[i];
    const last = i === keys.length - 1;
    const childOver = over == null ? undefined : over[key];
    const childBase = bas == null ? undefined : bas[key];
    const next = last ? value : walk(childOver, childBase, i + 1, sealed || Array.isArray(bas));

    if (Array.isArray(bas) || Array.isArray(over)) {
      const arr = Array.isArray(over) ? over.slice() : bas.slice();
      arr[Number(key)] = next;
      return arr;
    }
    if (bas === undefined && over === undefined && /^\d+$/.test(key)) {
      const arr = [];                       // no base to learn from: a numeric key still means a list
      arr[Number(key)] = next;
      return arr;
    }
    const seed = sealed && isObj(bas) ? bas : null;      // inside an array → carry the whole object
    return { ...(seed || {}), ...(isObj(over) ? over : {}), [key]: next };
  };
  return walk(overrides || {}, base, 0, false);
}

/** the array a leaf belongs to, if any: "landing.quad.items.2.name" → "landing.quad.items" (index 2) */
export function arrayPathOf(path) {
  const keys = String(path).split(".");
  for (let i = keys.length - 1; i >= 0; i--) {
    if (/^\d+$/.test(keys[i])) return { arrayPath: keys.slice(0, i).join("."), index: Number(keys[i]) };
  }
  return null;
}

/** move an item inside an array value */
export function reorder(list, from, to) {
  const a = (list || []).slice();
  if (from < 0 || from >= a.length || to < 0 || to >= a.length || from === to) return a;
  const [item] = a.splice(from, 1);
  a.splice(to, 0, item);
  return a;
}

/**
 * Which leaf produced this element?
 *
 *  · `text`  the element's own trimmed text (or an image/video src)
 *  · `all`   every element in document order that carries the same text — used for the ordinal
 *
 * Returns { path, value, kind, candidates } or null. `candidates` > 1 means the UI should ask.
 */
export function resolveByValue(map, text, ordinal = 0) {
  const hits = map.get(normalise(text));
  if (!hits || !hits.length) return null;
  const leaf = hits[Math.min(ordinal, hits.length - 1)];
  return { path: leaf.path, value: leaf.value, kind: leaf.kind, candidates: hits.length };
}
